import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_ORIGIN = API_URL.replace(/\/api$/, '');

/** Resolve a stored upload path (/uploads/...) to a full fetchable URL. */
export const fileUrl = (p) => {
  if (!p) return '';
  if (/^https?:\/\//.test(p)) return p;
  return API_ORIGIN + p;
};

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - add auth token + impersonation header
api.interceptors.request.use(config => {
  const token = localStorage.getItem('ledgerpro_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // When impersonating a company, send the company ID header
  try {
    const imp = JSON.parse(localStorage.getItem('ledgerpro_impersonate') || 'null');
    if (imp?.companyId) {
      config.headers['X-View-Company'] = imp.companyId;
    }
  } catch { /* */ }
  return config;
}, error => Promise.reject(error));

// Response interceptor
api.interceptors.response.use(
  response => response.data,
  async error => {
    const code = error.response?.data?.code;
    // Subscription gate: no plan chosen or trial expired → system unusable, log out.
    // BUT verify against the server first — a single spurious 403 (e.g. during a
    // plan change or a stale token race) must not destroy an otherwise valid session.
    if (code === 'SUBSCRIPTION_REQUIRED' || code === 'TRIAL_EXPIRED') {
      if (localStorage.getItem('ledgerpro_token') && !error.config?._subscriptionVerified) {
        let confirmed = true;
        try {
          const token = localStorage.getItem('ledgerpro_token');
          const resp = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (resp.ok) {
            const me = await resp.json();
            confirmed = !!me.access_state && me.access_state !== 'ok';
          }
        } catch { /* verification failed → treat as confirmed */ }
        if (confirmed) {
          localStorage.removeItem('ledgerpro_token');
          localStorage.removeItem('ledgerpro_user');
          sessionStorage.setItem('ledgerpro_lock_reason', error.response?.data?.error || 'Your subscription is inactive.');
          window.location.href = '/login';
        } else {
          error.config._subscriptionVerified = true;
        }
      }
    }
    if (error.response?.status === 401) {
      const hadToken = !!localStorage.getItem('ledgerpro_token');
      localStorage.removeItem('ledgerpro_token');
      localStorage.removeItem('ledgerpro_user');
      // Only redirect if the user actually had a token (expired/invalid).
      // If no token, let the caller handle the error gracefully.
      if (hadToken && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    const message = error.response?.data?.error || error.message || 'Request failed';
    return Promise.reject(new Error(message));
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  phoneLogin: (data) => api.post('/auth/phone/login', data),
  googleLogin: (data) => api.post('/auth/google/login', data),
  googleRegister: (data) => api.post('/auth/google/register', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  uploadAvatar: (image) => api.post('/auth/avatar', { image }),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  checkEmail: (email) => api.get('/auth/check-email', { params: { email } }),
  // Superadmin impersonation
  impersonate: (companyId) => api.post(`/auth/impersonate/${companyId}`),
};

// Accounts
export const accountsAPI = {
  list: (params) => api.get('/accounts', { params }),
  tree: () => api.get('/accounts/tree'),
  types: () => api.get('/accounts/types'),
  get: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  balance: (id, params) => api.get(`/accounts/${id}/balance`, { params }),
  ledger: (id, params) => api.get(`/accounts/${id}/ledger`, { params }),
};

// Journal
export const journalAPI = {
  list: (params) => api.get('/journal', { params }),
  get: (id) => api.get(`/journal/${id}`),
  create: (data) => api.post('/journal', data),
  post: (id) => api.post(`/journal/${id}/post`),
  void: (id, data) => api.post(`/journal/${id}/void`, data),
  trialBalance: (params) => api.get('/journal/reports/trial-balance', { params }),
};

// Customers
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  remove: (id) => api.delete(`/customers/${id}`),
  balance: (id) => api.get(`/customers/${id}/balance`),
  transactions: (id) => api.get(`/customers/${id}/transactions`),
  statement: (id, params) => api.get(`/customers/${id}/statement`, { params }),
  types: () => api.get('/customers/types/list'),
  aging: () => api.get('/customers/reports/aging'),
};

// Vendors
export const vendorsAPI = {
  list: (params) => api.get('/vendors', { params }),
  get: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  remove: (id) => api.delete(`/vendors/${id}`),
  aging: () => api.get('/vendors/reports/aging'),
};

// Invoices
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  send: (id) => api.post(`/invoices/${id}/send`),
  void: (id, data) => api.post(`/invoices/${id}/void`, data),
  remove: (id) => api.delete(`/invoices/${id}`),
};

// Bills
export const billsAPI = {
  list: (params) => api.get('/bills', { params }),
  get: (id) => api.get(`/bills/${id}`),
  create: (data) => api.post('/bills', data),
  update: (id, data) => api.put(`/bills/${id}`, data),
  remove: (id) => api.delete(`/bills/${id}`),
  submit: (id) => api.post(`/bills/${id}/submit`),
  void: (id, data) => api.post(`/bills/${id}/void`, data),
};

// Payments
export const paymentsAPI = {
  customerList: (params) => api.get('/payments/customers', { params }),
  customerGet: (id) => api.get(`/payments/customers/${id}`),
  customerCreate: (data) => api.post('/payments/customers', data),
  customerUpdate: (id, data) => api.put(`/payments/customers/${id}`, data),
  customerVoid: (id, data) => api.post(`/payments/customers/${id}/void`, data),
  customerDelete: (id) => api.delete(`/payments/customers/${id}`),
  vendorList: (params) => api.get('/payments/vendors', { params }),
  vendorGet: (id) => api.get(`/payments/vendors/${id}`),
  vendorCreate: (data) => api.post('/payments/vendors', data),
  vendorUpdate: (id, data) => api.put(`/payments/vendors/${id}`, data),
  vendorVoid: (id, data) => api.post(`/payments/vendors/${id}/void`, data),
  vendorDelete: (id) => api.delete(`/payments/vendors/${id}`),
};

// Banking
export const bankingAPI = {
  accounts: () => api.get('/banking/accounts'),
  createAccount: (data) => api.post('/banking/accounts', data),
  updateAccount: (id, data) => api.put(`/banking/accounts/${id}`, data),
  transactions: (params) => api.get('/banking/transactions', { params }),
  createDeposit: (data) => api.post('/banking/deposits', data),
  updateTransaction: (id, data) => api.put(`/banking/transactions/${id}`, data),
  deleteTransaction: (id) => api.delete(`/banking/transactions/${id}`),
  createTransfer: (data) => api.post('/banking/transfers', data),
  reconciliations: () => api.get('/banking/reconciliations'),
  createReconciliation: (data) => api.post('/banking/reconciliations', data),
  completeReconciliation: (id, data) => api.put(`/banking/reconciliations/${id}/complete`, data),
  createCheck: (data) => api.post('/banking/checks', data),
};

// Expenses
export const expensesAPI = {
  list: (params) => api.get('/expenses', { params }),
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  approve: (id) => api.post(`/expenses/${id}/approve`),
  categories: () => api.get('/expenses/categories/list'),
  createCategory: (data) => api.post('/expenses/categories', data),
};

// Products
export const productsAPI = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  remove: (id) => api.delete(`/products/${id}`),
  categories: () => api.get('/products/categories/list'),
  createCategory: (data) => api.post('/products/categories', data),
  uom: () => api.get('/products/uom/list'),
};

// Inventory
export const inventoryAPI = {
  list: (params) => api.get('/inventory', { params }),
  transactions: (params) => api.get('/inventory/transactions', { params }),
  adjust: (data) => api.post('/inventory/adjustments', data),
  transfer: (data) => api.post('/inventory/transfers', data),
  valuation: () => api.get('/inventory/valuation'),
  warehouses: () => api.get('/inventory/warehouses'),
  createWarehouse: (data) => api.post('/inventory/warehouses', data),
};

// Reports
export const reportsAPI = {
  profitLoss: (params) => api.get('/reports/profit-loss', { params }),
  balanceSheet: (params) => api.get('/reports/balance-sheet', { params }),
  trialBalance: (params) => api.get('/reports/trial-balance', { params }),
  arAging: () => api.get('/reports/ar-aging'),
  apAging: () => api.get('/reports/ap-aging'),
  generalLedger: (params) => api.get('/reports/general-ledger', { params }),
  salesByCustomer: (params) => api.get('/reports/sales-by-customer', { params }),
  salesByProduct: (params) => api.get('/reports/sales-by-product', { params }),
  expenseReport: (params) => api.get('/reports/expense-report', { params }),
  cashFlow: (params) => api.get('/reports/cash-flow', { params }),
  taxReport: (params) => api.get('/reports/tax-report', { params }),
  journal: (params) => api.get('/reports/journal', { params }),
  inventoryValuation: () => api.get('/reports/inventory-valuation'),
  customerBalances: () => api.get('/reports/customer-balances'),
  vendorBalances: () => api.get('/reports/vendor-balances'),
  retainedEarnings: () => api.get('/reports/retained-earnings'),
  purchasesByVendor: (params) => api.get('/reports/purchases-by-vendor', { params }),
  invoiceList: (params) => api.get('/reports/invoice-list', { params }),
};

// Dashboard
export const dashboardAPI = {
  get: (params) => api.get('/dashboard', { params }),
};

// Fiscal
export const fiscalAPI = {
  years: () => api.get('/fiscal/years'),
  createYear: (data) => api.post('/fiscal/years', data),
  closeYear: (id) => api.post(`/fiscal/years/${id}/close`),
  periods: () => api.get('/fiscal/periods'),
  closePeriod: (id) => api.post(`/fiscal/periods/${id}/close`),
};

// Tax
export const taxAPI = {
  rates: () => api.get('/tax/rates'),
  createRate: (data) => api.post('/tax/rates', data),
  updateRate: (id, data) => api.put(`/tax/rates/${id}`, data),
  groups: () => api.get('/tax/groups'),
  createGroup: (data) => api.post('/tax/groups', data),
};

// Assets
export const assetsAPI = {
  list: (params) => api.get('/assets', { params }),
  get: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  depreciate: (id) => api.post(`/assets/${id}/depreciate`),
  dispose: (id, data) => api.post(`/assets/${id}/dispose`, data),
  categories: () => api.get('/assets/categories/list'),
  createCategory: (data) => api.post('/assets/categories', data),
};

// Audit
export const auditAPI = {
  list: (params) => api.get('/audit', { params }),
};

// Backup — proprietary encrypted .ledgerpro format
export const backupAPI = {
  list: () => api.get('/backup'),
  create: (data) => api.post('/backup/create', data),
  download: (id) => api.get(`/backup/${id}/download`, { responseType: 'blob' }),
  restore: (file, fileName) => {
    // Raw octet-stream body; the server validates/decrypts internally
    return api.post('/backup/restore', file, {
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-File-Name': fileName || file.name || 'backup.ledgerpro',
      },
      timeout: 300000,
    });
  },
};

// Search
export const searchAPI = {
  search: (q) => api.get('/search', { params: { q } }),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  roles: () => api.get('/users/roles'),
  createRole: (data) => api.post('/users/roles', data),
  permissions: () => api.get('/users/permissions'),
  // Superadmin platform-level user management
  listAll: () => api.get('/users/all'),
  createAll: (data) => api.post('/users/all', data),
  updateAll: (id, data) => api.put(`/users/all/${id}`, data),
  deleteAll: (id) => api.delete(`/users/all/${id}`),
  // Superadmin: set profile picture for any user
  uploadAvatar: (id, image) => api.post(`/users/all/${id}/avatar`, { image }),
};

// Company
export const companyAPI = {
  current: () => api.get('/companies/current'),
  update: (data) => api.put('/companies/current', data),
  uploadLogo: (image) => api.post('/companies/logo', { image }),
  getLogo: (companyId) => api.get(`/companies/${companyId}/logo`),
  branches: () => api.get('/companies/branches'),
  createBranch: (data) => api.post('/companies/branches', data),
  settings: () => api.get('/companies/settings'),
  updateSettings: (data) => api.put('/companies/settings', data),
  currencies: () => api.get('/companies/currencies'),
  addCurrency: (data) => api.post('/companies/currencies', data),
  setBaseCurrency: (code) => api.post(`/companies/currencies/${code}/base`),
  // Superadmin: set base currency for a specific company
  setCompanyBaseCurrency: (companyId, code) => api.post(`/companies/${companyId}/base-currency`, { code }),
  exchangeRates: () => api.get('/companies/exchange-rates'),
  createExchangeRate: (data) => api.post('/companies/exchange-rates', data),
};

// Estimates
export const estimatesAPI = {
  list: (params) => api.get('/estimates', { params }),
  get: (id) => api.get(`/estimates/${id}`),
  create: (data) => api.post('/estimates', data),
  update: (id, data) => api.put(`/estimates/${id}`, data),
  remove: (id) => api.delete(`/estimates/${id}`),
  convertToInvoice: (id, data) => api.post(`/estimates/${id}/convert-to-invoice`, data),
  convertToSalesOrder: (id) => api.post(`/estimates/${id}/convert-to-sales-order`),
};

// Sales Orders
export const salesOrdersAPI = {
  list: (params) => api.get('/sales-orders', { params }),
  get: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  remove: (id) => api.delete(`/sales-orders/${id}`),
  convertToInvoice: (id) => api.post(`/sales-orders/${id}/convert-to-invoice`),
  reopen: (id) => api.post(`/sales-orders/${id}/reopen`),
};

// Purchase Orders
export const purchaseOrdersAPI = {
  list: (params) => api.get('/purchase-orders', { params }),
  get: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  remove: (id) => api.delete(`/purchase-orders/${id}`),
  convertToBill: (id) => api.post(`/purchase-orders/${id}/convert-to-bill`),
  reopen: (id) => api.post(`/purchase-orders/${id}/reopen`),
};

// Employees
export const employeesAPI = {
  list: () => api.get('/employees'),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  remove: (id) => api.delete(`/employees/${id}`),
};

// Subscriptions (Superadmin)
export const subscriptionsAPI = {
  // Plans
  plans: () => api.get('/subscriptions/plans'),
  plansPublic: () => api.get('/subscriptions/plans/public'),
  createPlan: (data) => api.post('/subscriptions/plans', data),
  updatePlan: (id, data) => api.put(`/subscriptions/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/subscriptions/plans/${id}`),
  // Companies
  companies: (params) => api.get('/subscriptions/companies', { params }),
  assign: (data) => api.post('/subscriptions/assign', data),
  confirm: (id, data) => api.post(`/subscriptions/${id}/confirm`, data),
  reject: (id, data) => api.post(`/subscriptions/${id}/reject`, data),
  cancel: (id, data) => api.post(`/subscriptions/${id}/cancel`, data),
  reactivate: (id) => api.post(`/subscriptions/${id}/reactivate`),
  suspend: (id, data) => api.post(`/subscriptions/${id}/suspend`, data),
  unsuspend: (id) => api.post(`/subscriptions/${id}/unsuspend`),
  // Billing & Activity
  payInvoice: (id, data) => api.post(`/subscriptions/invoices/${id}/pay`, data),
  unpayInvoice: (id) => api.post(`/subscriptions/invoices/${id}/unpay`),
  updateInvoice: (id, data) => api.put(`/subscriptions/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/subscriptions/invoices/${id}`),
  deleteCompany: (id) => api.delete(`/subscriptions/companies/${id}`),
  invoices: (params) => api.get('/subscriptions/invoices', { params }),
  activity: () => api.get('/subscriptions/activity'),
  stats: () => api.get('/subscriptions/stats'),
  // Reports
  reports: (params) => api.get('/subscriptions/reports', { params }),
  // Company user
  my: () => api.get('/subscriptions/my'),
  renew: (data) => api.post('/subscriptions/renew', data),
};

// Email
export const emailAPI = {
  send: (data) => api.post('/email/send', data),
  config: () => api.get('/email/config'),
  log: () => api.get('/email/log'),
};

// Recurring Schedules
export const recurringAPI = {
  list: (params) => api.get('/recurring', { params }),
  get: (id) => api.get(`/recurring/${id}`),
  stats: () => api.get('/recurring/stats'),
  create: (data) => api.post('/recurring', data),
  update: (id, data) => api.put(`/recurring/${id}`, data),
  generate: (id) => api.post(`/recurring/${id}/generate`),
  runDue: () => api.post('/recurring/run-due'),
  remove: (id) => api.delete(`/recurring/${id}`),
};

// Platform (superadmin settings + notifications)
export const platformAPI = {
  getSettings: () => api.get('/platform/settings'),
  updateSettings: (data) => api.put('/platform/settings', data),
  uploadLogo: (image) => api.post('/platform/logo', { image }),
  uploadFavicon: (image) => api.post('/platform/favicon', { image }),
  getPublicSettings: () => api.get('/platform/public'),
  // Landing page CMS (public read, superadmin write)
  getLanding: () => api.get('/platform/landing'),
  updateLanding: (data) => api.put('/platform/landing', data),
  uploadOgImage: (image) => api.post('/platform/landing/og-image', { image }),
  // Chat (superadmin)
  getChatSessions: () => api.get('/platform/chat'),
  getChatMessages: (sessionId) => api.get(`/platform/chat/${sessionId}`),
  replyToChat: (sessionId, message) => api.post('/platform/chat/reply', { sessionId, message }),
  markChatRead: (sessionId) => api.put(`/platform/chat/read/${sessionId}`),
  deleteChat: (sessionId) => api.delete(`/platform/chat/${sessionId}`),
  // Legal pages (public, no auth)
  getPublicLegal: (slug) => api.get(`/legal/public/${slug}`),
  getPublicLegalAll: () => api.get('/legal/public'),
  getNotifications: () => api.get('/platform/notifications'),
  markRead: (id) => api.put(`/platform/notifications/${id}/read`),
  markAllRead: () => api.put('/platform/notifications/read-all'),
  createNotification: (data) => api.post('/platform/notifications', data),
};

export default api;
